import React, { useState, useContext, useRef } from "react";
import { Helmet } from "react-helmet";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import ManageSidebar from "../ManageSidebar";
import { FoodContext } from "../../../../../contexts/Food";
import { SettingsContext } from "../../../../../contexts/Settings";
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { _t, customStyle, getCookie, tableLoading } from "../../../../../functions/Functions";
import { BASE_URL } from "../../../../../BaseUrl";
import axios from "axios";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import { useReactToPrint } from "react-to-print";
import { useGeneralHooks } from "../../../../../hooks/generalHooks";

const DemandGeneration = () => {
  const { t } = useTranslation();
  
  // Contexts
  const { loading: contextLoading, setLoading } = useContext(SettingsContext);
  const { foodForPOS, propertyItemForSearch } = useContext(FoodContext);
  const { branchForSearch } = useContext(RestaurantContext);
  
  const { menuWisePrice } = useGeneralHooks();

  // States
  const [formData, setFormData] = useState({
    radio: "item",
    selectedBranches: [],
    selectedItems: [],
    days: "",
  });
  const [gridItems, setGridItems] = useState([]);
  
  // Response data
  const [reportData, setReportData] = useState(null);

  // Handlers
  const handleTypeChange = (e) => {
    setFormData({ radio: e.target.value, selectedItems: [], days: "" });
  };

  const handleItemChange = (items) => {
    setFormData((prev) => ({ ...prev, selectedItems: items || [] }));
  };

  const handleAdd = async () => {
    const { radio, selectedBranches, selectedItems, days } = formData;
    if (!selectedBranches || selectedBranches.length === 0) {
      toast.error(_t(t("Please select at least one branch")), { className: "text-center toast-notification" });
      return;
    }
    if (!selectedItems || selectedItems.length === 0) {
      toast.error(_t(t("Please select at least one item or property")), { className: "text-center toast-notification" });
      return;
    }
    if (!days || days <= 0) {
      toast.error(_t(t("Please enter valid days")), { className: "text-center toast-notification" });
      return;
    }

    setLoading(true);
    const url = BASE_URL + "/settings/forecast-items";
    const payload = {
      branch_id: selectedBranches.map(b => b.id),
      days: parseInt(days),
      items: selectedItems.map(item => ({
        item_id: item.id,
        type: radio
      }))
    };

    try {
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setLoading(false);
      
      if (res.data && res.data.success && res.data.data && res.data.data.items_forecast) {
        const forecastItems = res.data.data.items_forecast.map(item => ({
          item_id: item.item_id,
          name: item.item_name,
          type: item.type,
          qty: parseFloat(item.qty || 0),
          variation_id: item.variation_id || 0,
          variation_name: item.variation_name || "",
        }));

        setGridItems(prev => [...prev, ...forecastItems]);
        setFormData(prev => ({ ...prev, selectedItems: [], days: "" }));
      } else {
        toast.error(_t(t("Invalid response from server")), { className: "text-center toast-notification" });
      }
    } catch (error) {
      setLoading(false);
      toast.error(_t(t("Failed to fetch forecast items")), { className: "text-center toast-notification" });
    }
  };

  const handleRemove = (index) => {
    const newItems = [...gridItems];
    newItems.splice(index, 1);
    setGridItems(newItems);
  };

  const handleGenerate = async () => {
    if (gridItems.length === 0) {
      toast.error(_t(t("Please add at least one item to generate")), { className: "text-center toast-notification" });
      return;
    }

    setLoading(true);
    const url = BASE_URL + "/settings/demand-forecast";

    const payload = {
      branch_id: formData.selectedBranches.map(b => b.id),
      items: gridItems
    };

    try {
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setLoading(false);
      
      if (res.data) {
        setReportData(res.data.data);
        toast.success(_t(t("Demand Forecast Generated")), { className: "text-center toast-notification" });
        setTimeout(() => {
          if (componentRef.current) {
            handlePrint();
          }
        }, 500);
      }
    } catch (error) {
      setLoading(false);
      toast.error(_t(t("Failed to generate demand forecast")), { className: "text-center toast-notification" });
    }
  };

  const handleClearAll = () => {
    setGridItems([]);
    setFormData(prev => ({ ...prev, selectedBranches: [], selectedItems: [], days: "", radio: "item" }));
    setReportData(null);
  };

  // Print Logic
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { margin: 20px }`
  });

  return (
    <>
      <Helmet>
        <title>{_t(t("Demand & Generation"))}</title>
      </Helmet>
      
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    
                    <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                      <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb">
                          <li className="fk-breadcrumb__list">
                            <span className="t-link fk-breadcrumb__link text-capitalize">
                              {_t(t("Demand & Generation"))}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="row card p-2 mx-2 mb-3 table-text">
                      <div className="col-12">
                        {/* Input Row */}
                        <div className="row align-items-end mt-2 mb-3">
                          <div className="form-group col-md-3">
                            <div className="mb-2">
                              <label className="control-label">
                                {_t(t("Branch"))}
                                <span className="text-danger">*</span>
                              </label>
                            </div>
                            <Select
                              isMulti
                              options={branchForSearch || []}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              classNamePrefix="select"
                              onChange={(branches) => setFormData(prev => ({ ...prev, selectedBranches: branches || [] }))}
                              maxMenuHeight="200px"
                              styles={customStyle}
                              value={formData.selectedBranches}
                              placeholder={_t(t("Select")) + ".."}
                              isDisabled={gridItems.length > 0}
                            />
                          </div>

                          <div className="form-group col-md-2 mb-1">
                            <div className="mb-2">
                              <label className="control-label">
                                Type
                                <span className="text-danger">*</span>
                              </label>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="form-check mr-2">
                                <input className="form-check-input" type="radio" name="type" value="item" id="radioItem" checked={formData.radio === "item"} onChange={handleTypeChange} style={{width: "20px", height:"20px"}}/> 
                                <label className="form-check-label mt-1 mx-1" htmlFor="radioItem"> Item </label>
                              </div>
                              <div className="form-check mr-2">
                                <input className="form-check-input" type="radio" name="type" value="property" id="radioProperty" checked={formData.radio === "property"} onChange={handleTypeChange} style={{width: "20px", height:"20px"}}/>
                                <label className="form-check-label mt-1 mx-1" htmlFor="radioProperty"> Property </label>
                              </div>
                            </div>
                          </div>

                          <div className="form-group col-md-4">
                            <div className="mb-2">
                              <label className="control-label">
                                {formData.radio === "item" ? _t(t("Items")) : _t(t("Property"))}
                                <span className="text-danger">*</span>
                              </label>
                            </div>
                            <Select
                              isMulti
                              options={
                                Array.isArray(formData.radio === "item" ? foodForPOS : propertyItemForSearch)
                                  ? (formData.radio === "item" ? foodForPOS : propertyItemForSearch)
                                  : []
                              }
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              classNamePrefix="select"
                              onChange={handleItemChange}
                              maxMenuHeight="200px"
                              styles={customStyle}
                              value={formData.selectedItems}
                              placeholder={_t(t("Please Select")) + ".."}
                            />
                          </div>

                          <div className="form-group col-md-1">
                            <div className="mb-2">
                              <label className="control-label">
                                {_t(t("Days"))} <span className="text-danger">*</span>
                              </label>
                            </div>
                            <input 
                              type="number"
                              className="form-control px-2 table-text"
                              value={formData.days}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormData(prev => ({ ...prev, days: val }));
                              }}
                              placeholder="Days"
                              min="0"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAdd();
                                }
                              }}
                            />
                          </div>

                          <div className="form-group col-md-2">
                            <button disabled={contextLoading} type="button" className="btn table-text font-weight-md btn-success rounded-sm w-100" onClick={handleAdd}>
                              {_t(t("Add"))}
                            </button>
                          </div>
                        </div>

                        {/* Grid */}
                        {gridItems.length > 0 && (
                          <div className="mt-3">
                            <div className="table-responsive" style={{ maxHeight: "calc(100vh - 20rem)", overflowY: "auto" }}>
                              <table className="table table-bordered table-hover">
                              <thead className="align-middle">
                                <tr>
                                  <th className="table-text text-capitalize align-middle text-center border-1 border">
                                    {_t(t("Type"))}
                                  </th>
                                  <th className="table-text text-capitalize align-middle text-center border-1 border">
                                    {_t(t("Name"))}
                                  </th>
                                  <th className="table-text text-capitalize align-middle text-center border-1 border">
                                    {_t(t("Variation"))}
                                  </th>
                                  <th className="table-text text-capitalize align-middle text-center border-1 border">
                                    {_t(t("Qty"))}
                                  </th>
                                  <th className="table-text text-capitalize align-middle text-center border-1 border">
                                    {_t(t("Action"))}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="align-middle">
                                {gridItems.map((item, index) => (
                                  <tr key={index} className="align-middle">
                                    <td className="sm-text text-capitalize align-middle text-center">
                                      {item.type}
                                    </td>
                                    <td className="table-text text-capitalize align-middle text-center">
                                      {item.name}
                                    </td>
                                    <td className="sm-text text-capitalize align-middle text-center">
                                      {item.variation_name || "-"}
                                    </td>
                                    <td className="sm-text align-middle text-center">
                                      <input
                                        type="number"
                                        className="form-control table-text text-center mx-auto"
                                        style={{ width: "80px" }}
                                        value={item.qty}
                                        min="0"
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const newItems = [...gridItems];
                                          newItems[index].qty = val ? parseFloat(val) : "";
                                          setGridItems(newItems);
                                        }}
                                      />
                                    </td>
                                    <td className="table-text align-middle text-center">
                                      <MdDelete className="text-danger" style={{cursor: "pointer", fontSize: "20px"}} onClick={() => handleRemove(index)}/>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            
                            <div className="d-flex justify-content-end mt-3">
                              <button
                                disabled={contextLoading}
                                type="button"
                                className="btn btn-danger rounded mr-2"
                                onClick={handleClearAll}
                              >
                                {_t(t("Clear All"))}
                              </button>
                              <button
                                disabled={contextLoading}
                                type="button"
                                className="btn btn-primary rounded"
                                onClick={handleGenerate}
                              >
                                {_t(t("Generate"))}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hidden Report Data for Printing */}
                    <div className="d-none">
                      <div ref={componentRef}>
                        {reportData && reportData?.ingredients_required && reportData.ingredients_required && reportData.ingredients_required.length > 0 && (
                          <>
                            <h4 className="text-center mb-3">{_t(t("Ingredients Required (Demand Forecast)"))}</h4>
                            <table className="table table-bordered-sm table-hover">
                              <thead className="align-middle">
                                <tr>
                                  <th className="print-text-stock text-capitalize align-middle text-center border-dark border-1 border">
                                    {_t(t("Ingredient Name"))}
                                  </th>
                                  <th className="print-text-stock text-capitalize align-middle text-center border-dark border-1 border">
                                    {_t(t("Qty"))}
                                  </th>
                                  <th className="print-text-stock text-capitalize align-middle text-center border-dark border-1 border">
                                    {_t(t("Unit"))}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="align-middle">
                                {reportData.ingredients_required.map((ing, idx) => (
                                  <tr key={idx} className="align-middle">
                                    <td className="print-text-stock text-capitalize border-dark border-1 align-middle text-center">
                                      {ing.ingredient_name}
                                    </td>
                                    <td className="print-text-stock text-capitalize border-dark border-1 align-middle text-center">
                                      {ing.total_qty}
                                    </td>
                                    <td className="print-text-stock text-capitalize border-dark border-1 align-middle text-center">
                                      {ing.unit_name}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default DemandGeneration;
